/**
 * The controls panel: two players, four actions each, click a key to change
 * it. Changes are saved as they are made; the keyboard reads the saved
 * bindings live, so there is nothing to apply.
 */
import {
  ACTIONS,
  ACTION_LABELS,
  type Action,
  type Bindings,
  keyLabel,
  loadBindings,
  rebind,
  resetBindings,
  saveBindings,
} from '../games/volley/bindings.js';

const STYLE = `
.keys {
  position: fixed; inset: 0; z-index: 50;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  background: var(--bg, #0b0f18);
  font: 14px/1.5 var(--sans, system-ui, sans-serif);
  color: var(--fg, #e6ebf5);
}
.keys[hidden] { display: none !important; }
.keys .card { width: min(460px, 100%); max-height: 100%; overflow-y: auto; }
.keys h2 { margin: 0 0 2px; font-size: 20px; font-weight: 700; letter-spacing: -0.01em; }
.keys .muted { margin: 0 0 16px; color: var(--muted, #7f95b8); font-size: 13px; }
.keys .players { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.keys .player {
  padding: 12px; border-radius: var(--radius, 12px);
  background: var(--surface, rgba(255,255,255,0.06));
}
.keys .player h3 { margin: 0 0 8px; font-size: 12px; font-weight: 600; }
.keys .player.p1 h3 { color: #4fd1c5; }
.keys .player.p2 h3 { color: #f472b6; }
.keys .row { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 4px 0; }
.keys .row span { color: var(--muted, #7f95b8); font-size: 13px; }
.keys button {
  border: 0; border-radius: 10px;
  background: rgba(0,0,0,0.3); color: inherit;
  font: 600 13px var(--mono, monospace); cursor: pointer;
  min-width: 72px; padding: 7px 10px;
}
.keys button:hover { background: rgba(0,0,0,0.5); }
.keys button:focus-visible { outline: 2px solid var(--accent, #4fd1c5); outline-offset: 2px; }
.keys button.key[data-listening="true"] {
  background: var(--accent, #4fd1c5); color: var(--accent-ink, #062b28);
  font-family: var(--sans, system-ui, sans-serif); font-weight: 500;
}
.keys .actions { display: flex; gap: 8px; margin-top: 16px; }
.keys .actions button {
  flex: 1; padding: 11px 16px; border-radius: var(--radius, 12px);
  background: var(--surface, rgba(255,255,255,0.06)); font: 500 15px var(--sans, system-ui, sans-serif);
}
.keys .actions button:hover { background: var(--surface-hi, rgba(255,255,255,0.12)); }
.keys .actions .done { background: var(--accent, #4fd1c5); color: var(--accent-ink, #062b28); font-weight: 600; }
.keys .actions .done:hover { background: #6ee0d5; }
`;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<HTMLElementTagNameMap[K]> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  Object.assign(node, props);
  node.append(...children);
  return node;
}

export class BindingsPanel {
  readonly element: HTMLElement;
  private readonly card: HTMLElement;
  private bindings: Bindings = loadBindings();
  private listening: { player: 'p1' | 'p2'; action: Action } | null = null;
  private readonly onKey = (e: KeyboardEvent): void => this.capture(e);

  constructor(private readonly onClose: () => void) {
    if (!document.getElementById('keys-style')) {
      document.head.append(el('style', { id: 'keys-style', textContent: STYLE }));
    }
    this.card = el('div', { className: 'card' });
    this.element = el('div', { className: 'keys' }, this.card);
    this.element.hidden = true;
    document.body.append(this.element);
  }

  open(): void {
    this.bindings = loadBindings();
    this.listening = null;
    this.element.hidden = false;
    window.addEventListener('keydown', this.onKey, true);
    this.render();
  }

  close(): void {
    this.listening = null;
    this.element.hidden = true;
    window.removeEventListener('keydown', this.onKey, true);
    this.onClose();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey, true);
    this.element.remove();
  }

  private capture(e: KeyboardEvent): void {
    if (!this.listening) {
      if (e.code === 'Escape') this.close();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (e.code === 'Escape') {
      this.listening = null;
    } else {
      this.bindings = rebind(this.bindings, this.listening.player, this.listening.action, e.code);
      saveBindings(this.bindings);
      this.listening = null;
    }
    this.render();
  }

  private render(): void {
    const players = el('div', { className: 'players' });
    for (const [player, title] of [
      ['p1', 'Player 1 · Teal'],
      ['p2', 'Player 2 · Pink'],
    ] as const) {
      const box = el('div', { className: `player ${player}` }, el('h3', {}, title));
      for (const action of ACTIONS) {
        const listening =
          this.listening?.player === player && this.listening.action === action;
        const key = el(
          'button',
          { type: 'button', className: 'key' },
          listening ? 'press a key' : keyLabel(this.bindings[player][action]),
        );
        key.dataset['listening'] = String(listening);
        key.dataset['player'] = player;
        key.dataset['action'] = action;
        key.addEventListener('click', () => {
          this.listening = { player, action };
          this.render();
        });
        box.append(el('div', { className: 'row' }, el('span', {}, ACTION_LABELS[action]), key));
      }
      players.append(box);
    }

    const reset = el('button', { type: 'button', className: 'reset' }, 'Reset to defaults');
    reset.addEventListener('click', () => {
      this.bindings = resetBindings();
      this.render();
    });
    const done = el('button', { type: 'button', className: 'done' }, 'Done');
    done.addEventListener('click', () => this.close());

    this.card.replaceChildren(
      el('h2', {}, 'Controls'),
      el('p', { className: 'muted' }, 'Click a key to change it. Saved on this device.'),
      players,
      el('div', { className: 'actions' }, reset, done),
    );
  }
}
